import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import useMediaQuery from '@mui/material/useMediaQuery';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';

import { creerTheme } from '../theme/creerTheme';
import { useLanguage } from './LanguageContext';

/**
 * LES DEUX CACHES DE STYLE — construits UNE fois, au niveau module.
 *
 * 🔴 CE QUE FAIT `stylis-plugin-rtl`, ET CE QU'IL NE FAIT PAS. Il réécrit les
 * propriétés **physiques** du CSS émis : `margin-left` devient `margin-right`,
 * `padding-inline` s'inverse, `text-align: right` devient `left`, `left: 0`
 * devient `right: 0`. **Les ~130 sites directionnels du dépôt basculent donc
 * seuls — ils ne sont pas le travail de ce chantier.** *(Mesuré : 20 `ml`,
 * 27 `mr`, 9 `pl`, 8 `pr`, 64 `align="right"`, 4 positionnements.)*
 *
 * ⚠️ IL NE VOIT QUE DU CSS. Ce qui est décidé en JavaScript — le côté
 * d'ancrage d'un `Drawer`, le sens d'une flèche de pagination — passe par
 * `theme.direction`, pas par lui. Et ce qui n'est ni l'un ni l'autre — l'ordre
 * du **texte brut** — passe par `<html dir>`. *Trois couches distinctes ; la
 * plus fréquente erreur est de croire qu'une seule suffit.*
 *
 * 🔴 DEUX CACHES ET NON UN CACHE MUTABLE, ET C'EST LA CONDITION DE LA BASCULE
 * SANS RECHARGEMENT. Un cache Emotion **mémorise les règles déjà émises par
 * leur clé** : réinjecter le plugin dans un cache existant ne réécrirait pas
 * une seule des règles déjà insérées — l'application resterait à moitié
 * retournée, et rien ne le signalerait. Deux caches aux `key` DISTINCTES
 * (`mui` / `muirtl`) produisent deux espaces de noms de classes séparés :
 * changer de cache change l'intégralité de la feuille en un rendu.
 *
 * ⚠️ `prefixer` EST RÉINTRODUIT EXPLICITEMENT. Fournir `stylisPlugins` REMPLACE
 * la chaîne par défaut d'Emotion, qui contient le préfixeur. L'omettre ferait
 * perdre les préfixes fabricant **en hébreu seulement** — un défaut qui ne se
 * manifesterait que dans une langue, sur les navigateurs les plus anciens.
 */
const CACHES = {
  ltr: createCache({ key: 'mui' }),
  rtl: createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] }),
};

/**
 * Le mode d'affichage — clair, sombre, ou automatique.
 *
 * ── TROIS ÉTATS, PAS DEUX, ET LE TROISIÈME EST LE DÉFAUT ────────────────────
 *   · `clair`  — imposé par l'utilisateur ;
 *   · `sombre` — imposé par l'utilisateur ;
 *   · `auto`   — suit le réglage du SYSTÈME (`prefers-color-scheme`).
 *
 * ⚠️ `auto` est le défaut parce qu'un produit qui s'affiche en clair sur une
 * machine réglée en sombre force l'utilisateur à choisir pour retrouver ce
 * qu'il avait déjà choisi ailleurs. Le sélecteur des trois états est prévu à
 * l'écran Profil.
 *
 * ── OÙ VIT LA PRÉFÉRENCE, ET POURQUOI PAS DANS LE PROFIL ────────────────────
 * Dans `localStorage`, comme la LANGUE (`LanguageContext`) — même nature, même
 * rangement, et le précédent existe déjà dans ce dépôt.
 *
 * ⚠️ La mettre sur le compte serveur aurait voulu dire : une colonne, une
 * migration Alembic, une route, et surtout **un aller-retour réseau avant de
 * savoir quoi peindre**. L'écran de connexion s'afficherait alors dans le
 * mauvais mode puis basculerait — un clignotement, avant même qu'un compte
 * existe. Le mode est une préférence d'APPAREIL : la même personne peut vouloir
 * du sombre sur son portable le soir et du clair sur le poste de la clinique.
 * 🔴 Si un jour la préférence doit suivre le compte, c'est un lot à part —
 * il faudra alors peindre depuis `localStorage` puis réconcilier après
 * connexion, jamais attendre le réseau pour peindre.
 */

const CLE_STOCKAGE = 'mode-affichage';
const MODES = ['clair', 'sombre', 'auto'];

const ThemeModeContext = createContext(null);

export const useThemeMode = () => {
  const contexte = useContext(ThemeModeContext);
  if (!contexte) {
    throw new Error('useThemeMode doit être utilisé dans un ThemeModeProvider');
  }
  return contexte;
};

export const ThemeModeProvider = ({ children }) => {
  /**
   * 🔴 CE FOURNISSEUR LIT MAINTENANT LA LANGUE — DONC IL DOIT VIVRE SOUS
   * `LanguageProvider`, ET C'EST LE GESTE STRUCTUREL DU LOT.
   *
   * L'ordre était inverse : `ThemeModeProvider` dans `index.js`, au-dessus
   * d'`App`, et `LanguageProvider` à l'intérieur d'`App`. *Le thème ne pouvait
   * donc pas savoir dans quelle langue il peignait.*
   *
   * ⚠️ **LE SENS DE DÉPENDANCE EST CELUI-CI, PAS L'AUTRE** : la direction est
   * une propriété de la LANGUE ; le thème en dépend, jamais l'inverse. Faire
   * descendre la création du thème sous ses propres consommateurs aurait
   * inversé une dépendance pour éviter un déplacement — *on aurait payé la
   * même dette plus tard, avec des intérêts.*
   *
   * ⚠️ ET LE DÉPLACEMENT A ÉTÉ MESURÉ AVANT D'ÊTRE FAIT : les DEUX seuls
   * consommateurs du thème — `Profile.js` (`useThemeMode`) et `Dashboard.js`
   * (`useTheme`) — vivent sous `AppContent`, donc sous les deux fournisseurs
   * quel que soit leur ordre. **Personne ne lisait le thème entre les deux.**
   */
  const { direction } = useLanguage();

  // ⚠️ Lu À L'INITIALISATION, pas dans un `useEffect` : le lire après le premier
  // rendu ferait peindre l'application en clair puis basculer en sombre, à
  // chaque chargement. C'est le clignotement que ce contexte existe pour éviter.
  const [mode, setMode] = useState(() => {
    try {
      const enregistre = localStorage.getItem(CLE_STOCKAGE);
      return MODES.includes(enregistre) ? enregistre : 'auto';
    } catch {
      // `localStorage` peut lever (navigation privée verrouillée, stockage
      // désactivé). Une préférence d'affichage ne doit jamais empêcher
      // l'application de démarrer.
      return 'auto';
    }
  });

  const systemeEnSombre = useMediaQuery('(prefers-color-scheme: dark)');

  // Le mode EFFECTIF : ce qui est réellement peint. `auto` n'est pas une
  // couleur, c'est une délégation.
  const modeEffectif = mode === 'auto' ? (systemeEnSombre ? 'sombre' : 'clair') : mode;

  useEffect(() => {
    try {
      localStorage.setItem(CLE_STOCKAGE, mode);
    } catch {
      // Sans stockage, la préférence vaut pour la session. Rien de plus.
    }
  }, [mode]);

  const changerMode = (nouveau) => {
    if (MODES.includes(nouveau)) setMode(nouveau);
  };

  // ⚠️ Le thème n'est reconstruit QUE si le mode effectif change. Sans ce
  // mémo, chaque rendu produirait un nouvel objet de thème, donc une nouvelle
  // identité pour tous les styles émis — et MUI recalculerait l'intégralité de
  // la feuille à chaque frappe au clavier.
  const theme = useMemo(
    () => creerTheme(modeEffectif === 'sombre' ? 'dark' : 'light', direction),
    [modeEffectif, direction],
  );

  const valeur = useMemo(
    () => ({ mode, modeEffectif, changerMode, modesDisponibles: MODES }),
    [mode, modeEffectif],
  );

  return (
    <ThemeModeContext.Provider value={valeur}>
      {/* ⚠️ LE `CacheProvider` ENVELOPPE LE `ThemeProvider`, ET L'ORDRE COMPTE.
          Les styles des composants MUI sont émis par le cache d'Emotion ; s'il
          était à l'intérieur, `CssBaseline` — qui pose les règles de base sur
          `body` — serait émis par le cache PAR DÉFAUT d'Emotion, jamais
          retourné. La base de la page resterait en LTR sous une application
          en RTL. */}
      <CacheProvider value={CACHES[direction]}>
        <ThemeProvider theme={theme}>
          {/* `enableColorScheme` accorde les éléments natifs — barres de
              défilement, champs de formulaire du navigateur — au mode courant.
              Sans lui, une liste déroulante native reste blanche en mode sombre. */}
          <CssBaseline enableColorScheme />
          {children}
        </ThemeProvider>
      </CacheProvider>
    </ThemeModeContext.Provider>
  );
};

export default ThemeModeContext;
